import { useCallback, useEffect, useRef, useState } from "react";
import type { DraftCartLine, Product } from "@/lib/types";

export interface RemovalNotice {
  id: string;
  productName: string;
  /** Display name of the participant who removed the line (when known). */
  byNickname?: string;
}

export interface UseRemovalNotifierResult {
  notices: RemovalNotice[];
  dismiss: (id: string) => void;
  /**
   * Call synchronously **before** any local `removeLine` / `updateQty(..., 0)`.
   * The next RTDB snapshot in which the line disappears will then be silenced
   * for this participant — no toast.
   */
  markSelfRemoved: (lineId: string) => void;
}

/**
 * Detects lines I owned (`addedBy === myParticipantId`) that disappear from the
 * shared cart without me having triggered the deletion. Used to surface a toast
 * to the owner when another participant removes their line.
 */
export const useRemovalNotifier = (
  lines: Record<string, DraftCartLine>,
  myParticipantId: string,
  productsById: Map<string, Product>,
  suppress: boolean = false
): UseRemovalNotifierResult => {
  const prevLinesRef = useRef<Record<string, DraftCartLine>>({});
  const selfRemovedRef = useRef<Set<string>>(new Set());
  const [notices, setNotices] = useState<RemovalNotice[]>([]);

  useEffect(() => {
    if (suppress) {
      // A submit is in flight (or just settled): draftCart wipes here are part
      // of the legitimate "sent to kitchen" flow, not a participant removing a
      // line. Skip notice generation but still advance prevLinesRef so the next
      // genuine removal compares against the post-submit state.
      prevLinesRef.current = lines;
      return;
    }
    const prev = prevLinesRef.current;
    const fresh: RemovalNotice[] = [];
    for (const [lineId, line] of Object.entries(prev)) {
      if (lineId in lines) continue;
      if (line.addedBy !== myParticipantId) continue;
      if (selfRemovedRef.current.has(lineId)) {
        selfRemovedRef.current.delete(lineId);
        continue;
      }
      const product = productsById.get(line.productId);
      const productName =
        product?.name ?? line.productSnapshot?.name ?? line.productId;
      const byNickname = (line as { removedByName?: string }).removedByName;
      fresh.push({
        id: `${lineId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        productName,
        byNickname,
      });
    }
    if (fresh.length) setNotices((current) => [...current, ...fresh]);
    prevLinesRef.current = lines;
  }, [lines, myParticipantId, productsById, suppress]);

  const dismiss = useCallback((id: string): void => {
    setNotices((current) => current.filter((n) => n.id !== id));
  }, []);

  const markSelfRemoved = useCallback((lineId: string): void => {
    selfRemovedRef.current.add(lineId);
  }, []);

  return { notices, dismiss, markSelfRemoved };
};
